import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost } from "../../../lib";

const DROPIN_SRC = "https://js.braintreegateway.com/web/dropin/1.42.0/js/dropin.min.js";

const useStyles = makeStyles((theme) => ({
  body: { minWidth: 460 },
  amount: { marginBottom: theme.spacing(2) },
  warn: {
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 12.5,
    lineHeight: 1.55,
    background: "rgba(183,28,28,0.08)",
    color: "#b71c1c",
    border: "1px solid rgba(183,28,28,0.35)",
  },
  sandbox: {
    background: "rgba(224,168,0,0.14)",
    color: "#8a6100",
    border: "1px solid rgba(224,168,0,0.5)",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  ok: { color: "#1b5e20", fontSize: 13, marginTop: theme.spacing(1) },
}));

/** Loads the Drop-in script once, on first use. */
function loadDropin() {
  if (window.braintree?.dropin) return Promise.resolve();
  const existing = document.querySelector(`script[src="${DROPIN_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", resolve);
      existing.addEventListener("error", reject);
    });
  }
  return new Promise((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = DROPIN_SRC;
    tag.onload = resolve;
    tag.onerror = () => reject(new Error("Could not load the card form."));
    document.body.appendChild(tag);
  });
}

/**
 * Takes a card payment against an existing order.
 *
 * The card is entered into Braintree's own iframes and exchanged there for a
 * single-use nonce; only that nonce reaches our server. No card number, expiry
 * or CVV passes through this app, which is what keeps it out of scope.
 */
export function OrderCaptureDialog({ orderId, defaultAmount, onClose, onCaptured }) {
  const classes = useStyles();
  const mountRef = useRef(null);
  const instanceRef = useRef(null);

  const [amount, setAmount] = useState(String(defaultAmount ?? ""));
  const [env, setEnv] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data } = await authGet("/orders/payment/client-token");
      if (cancelled) return;

      if (!data?.available) {
        setError(data?.reason || "Card payments are not configured.");
        return;
      }
      setEnv(data.environment);

      try {
        await loadDropin();
        if (cancelled) return;
        const instance = await window.braintree.dropin.create({
          authorization: data.clientToken,
          container: mountRef.current,
        });
        if (cancelled) {
          instance.teardown();
          return;
        }
        instanceRef.current = instance;
        setReady(true);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load the card form.");
      }
    })();

    return () => {
      cancelled = true;
      // Drop-in leaves iframes behind if it is not torn down.
      instanceRef.current?.teardown?.().catch(() => {});
      instanceRef.current = null;
    };
  }, []);

  const charge = async () => {
    const value = Number(amount);
    if (!(value > 0)) {
      setError("Enter an amount greater than zero.");
      return;
    }

    setBusy(true);
    setError("");

    let nonce;
    try {
      ({ nonce } = await instanceRef.current.requestPaymentMethod());
    } catch (e) {
      setBusy(false);
      setError("Check the card details and try again.");
      return;
    }

    const { data, error: err } = await authPost(`/orders/${orderId}/payment/capture`, {
      nonce,
      amount: value,
    });
    setBusy(false);

    if (err) {
      setError(err.message || "The payment could not be taken.");
      return;
    }
    if (data?.captured === false) {
      setError(data.reason || "The payment was declined.");
      return;
    }

    setDone(
      `Charged ${data.amount} to ${data.cardType ?? "card"} ending ${data.last4 ?? "••••"}. ` +
        `Transaction ${data.transactionId}.`,
    );
    onCaptured();
  };

  const live = env === "production";

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm">
      <DialogTitle>Take Payment</DialogTitle>
      <DialogContent>
        <div className={classes.body}>
          {env && (
            <div className={`${classes.warn} ${live ? "" : classes.sandbox}`}>
              {live ? (
                <>
                  <strong>This charges a real card.</strong> The amount below will
                  be captured immediately.
                </>
              ) : (
                <>
                  <strong>Sandbox.</strong> No money moves; test cards only.
                </>
              )}
            </div>
          )}

          <TextField
            className={classes.amount}
            label="Amount"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={busy || !!done}
          />

          <div ref={mountRef} />

          {error && <div className={classes.error}>{error}</div>}
          {done && <div className={classes.ok}>{done}</div>}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy} size="small">
          {done ? "Close" : "Cancel"}
        </Button>
        <Button
          onClick={charge}
          disabled={!ready || busy || !!done}
          size="small"
          variant="contained"
          color="primary"
        >
          {busy ? "Charging…" : "Charge card"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderCaptureDialog;
